import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URLS } from "../App";
import api from "../hooks/api";
import ListItems from "../components/UserManagementPages/ListItems";
import ManageUser from "../components/UserManagementPages/ManageUser";
import ResetPassword from "../components/UserManagementPages/ResetPassword";
import CheckUser from "../components/UserManagementPages/CheckUser";
import { useLayoutContext } from "../context/LayoutContext";

const UsersManagementPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    userSettings,
    setUserSettings,
    systemError,
    setSystemError,
    loading,
    setLoading,
    error,
    setError,
    success,
    setSuccess,
    modalScreen,
    setModalScreen,
    page,
    setPage,
  } = useLayoutContext();
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [commercialAgentFilter, setCommercialAgentFilter] =
    useState<string>("");
  const [perPage, setPerPage] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>("username");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [totalPages, setTotalPages] = useState(1);
  const [remainingSubaccountsAllowed, setRemainingSubaccountsAllowed] =
    useState<number>(userSettings?.profile.remainingSubaccountsAllowed ?? 0);

  const fetchUsers = async () => {
    if (users.length === 0) {
      setLoading(true);
    }
    setError(null);
    setSuccess(null);
    try {
      const response = await api.get(
        API_URLS.USERS +
          `?search=` +
          searchTerm +
          `&role=` +
          (roleFilter || "") +
          `&commercial_agent=` +
          (commercialAgentFilter || "") +
          `&sort_by=` +
          sortBy +
          `&sort_order=` +
          sortOrder +
          `&page=` +
          page +
          `&per_page=${perPage}`,
      );
      setUsers(response.data.users || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error: any) {
      console.warn("Error fetching users:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = useCallback(
    (searchTerm: string) => {
      setSearchTerm(searchTerm);
      setPage(1);
      history.pushState(history.state, "", pathname + `?page=1`);
    },
    [pathname, setPage],
  );

  const handleUserUpdated = useCallback(
    async (user: User) => {
      const existingUser = users.find((u) => u.id === user.id);
      if (!existingUser) {
        setUsers((prevUsers) => [...prevUsers, user]);
        setSuccess(`Utilizador ${user.username} criado com sucesso!`);
        setRemainingSubaccountsAllowed(remainingSubaccountsAllowed - 1);
      } else {
        setUsers((prevUsers) => {
          return prevUsers.map((u) => (u.id === user.id ? user : u));
        });
        setSuccess(`Utilizador ${user.username} atualizado com sucesso!`);
      }
    },
    [
      users,
      setSuccess,
      setRemainingSubaccountsAllowed,
      remainingSubaccountsAllowed,
    ],
  );

  const handleUserDeleted = (user: User) => {
    const newUsers = users.filter((u) => u.id !== user.id);
    setUsers(newUsers);
    setSuccess(`Utilizador ${user.username} apagado com sucesso!`);
    if (newUsers.length === 0 && page > 1) {
      setPage(page - 1);
      history.pushState(history.state, "", pathname + `?page=${page - 1}`);
    }
  };

  const openManageUser = (user: User) => {
    setModalScreen({
      state: "edit",
      title: user.id
        ? `Gerir utilizador: #${user.username}`
        : "Criar novo utilizador",
      children: (
        <ManageUser
          user={user}
          onUserUpdate={handleUserUpdated}
          isAdmin={userSettings?.isAdmin() ?? false}
          isMainAccount={userSettings?.isMainAccount() ?? false}
          setError={setError}
          setSuccess={setSuccess}
        />
      ),
      onClose: () => {
        setModalScreen(null);
      },
    });
  };

  const openPasswordReset = async (user: User) => {
    setModalScreen({
      state: "confirm",
      title: `Alterar senha para utilizador ${user.username}`,
      className: "max-w-[400px] max-h-[37vh] bg-white",
      children: (
        <ResetPassword
          user={user}
          onResetPassword={(updatedUser: User) => {
            setSuccess("Senha redefinida com sucesso!");
            setModalScreen(null);
            handleUserUpdated(updatedUser);
          }}
        />
      ),
      onClose: () => {
        setModalScreen(null);
      },
    });
  };

  const openMfaReset = (user: User) => {
    setModalScreen({
      state: "confirm",
      title: `Repor MFA para utilizador ${user.username}`,
      className: "max-w-[420px] bg-white",
      children: (
        <div className="p-4 text-center">
          <p className="mb-3 text-gray-800 font-medium">
            Tem certeza de que deseja repor a verificação em dois passos (MFA)
            para <strong>{user.username}</strong>?
          </p>
          <p className="text-xs text-gray-500 mb-6">
            O utilizador terá de reconfigurar a sua aplicação de autenticação no
            próximo login.
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              onClick={() => setModalScreen(null)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
              onClick={async () => {
                try {
                  const res = await api.post(
                    `${API_URLS.USERS}${user.id}/_actions/reset_mfa`,
                  );
                  setSuccess(
                    `MFA do utilizador ${user.username} reposto com sucesso!`,
                  );
                  setModalScreen(null);
                  handleUserUpdated(res.data);
                } catch {
                  setError("Não foi possível repor o MFA do utilizador.");
                  setModalScreen(null);
                }
              }}
            >
              Confirmar Reposição
            </button>
          </div>
        </div>
      ),
      onClose: () => setModalScreen(null),
    });
  };

  const openCheckUser = () => {
    if (!userSettings?.isAdmin()) {
      setSystemError({
        message:
          "Apenas utilizadores administradores podem validar utilizadores.",
        code: "NOT_AUTHORIZED",
        details:
          "Tentativa de validação de utilizador por um utilizador não administrador.",
        retryAfter: () => {
          setModalScreen(null);
        },
      });
      return;
    }
    setModalScreen({
      state: "edit",
      title: "Validar utilizador",
      children: <CheckUser setError={setError} setSuccess={setSuccess} />,
      onClose: () => setModalScreen(null),
    });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    history.pushState(history.state, "", `/users?page=${newPage}`);
  };

  useEffect(() => {
    if (isAuthenticated()) {
      fetchUsers();
    }
  }, [
    userSettings,
    page,
    perPage,
    searchTerm,
    roleFilter,
    commercialAgentFilter,
    sortBy,
    sortOrder,
  ]);

  if (
    !userSettings?.isAdmin() &&
    !userSettings?.isMainAccount() &&
    !userSettings?.isCommercial()
  ) {
    return (
      <div className="container mx-auto p-4">
        Apenas administradores e contas principais podem gerenciar utilizadores.
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex flex-col gap-6">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <span className="text-3xl font-bold text-gray-900 tracking-tight">
                Utilizadores
              </span>
              <p className="text-gray-500 mt-1">
                Gerencie utilizadores e permissões do sistema.
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-auto">
            {loading ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  <p className="mt-4 text-gray-600">
                    A carregar utilizadores...
                  </p>
                </div>
              </div>
            ) : (
              <ListItems
                data={users}
                userProfile={userSettings}
                searchTerm={searchTerm}
                setSearchTerm={handleSearchChange}
                roleFilterSelected={roleFilter}
                setRoleFilterSelected={setRoleFilter}
                commercialAgentFilterSelected={commercialAgentFilter}
                setCommercialAgentFilterSelected={setCommercialAgentFilter}
                currentPage={page}
                perPage={perPage}
                setPerPage={setPerPage}
                totalPages={totalPages}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                remainingItems={remainingSubaccountsAllowed}
                setError={setError}
                onPageChange={handlePageChange}
                setCheckUser={openCheckUser}
                handleManageItem={openManageUser}
                handleDeleteItem={handleUserDeleted}
                handlePasswordReset={openPasswordReset}
                handleMfaReset={openMfaReset}
                handleCreateItem={() =>
                  openManageUser({
                    type: userSettings?.isMainAccount() ? "reseller" : "guest",
                    credentialsExpired: true,
                    enabled: true,
                    erpUserReference: userSettings?.isMainAccount()
                      ? userSettings?.client?.id
                      : null,
                    client: userSettings?.isMainAccount()
                      ? userSettings?.client
                      : null,
                  } as User)
                } // Pass an empty user object to create a new user
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UsersManagementPage;
